/**
 * Palette de commandes (⌘K / Ctrl-K) — accès direct à tout écran par recherche floue.
 * Antidote à « je ne trouve pas l'outil » : on tape, on va.
 *
 * Lot SR du chantier 0.5.x (revue par métier, manque 7 : SM-19, PO-19) : la même palette cherche
 * aussi les OBJETS métier — par identifiant (`#12`, `ANO-3`, `TC-…`, `NFR-…`, `ADR-1`, `1.1.0`)
 * ou par texte — via `GET /api/v1/search`, sur les seuls projets et programmes dont l'utilisateur
 * est membre (la passerelle applique ce besoin d'en connaître). Les résultats sont groupés par type
 * et s'ouvrent sur leur page de détail, dans leur projet ou leur programme. Clavier et lecteurs
 * d'écran : combobox et listbox de cmdk ; l'état de la recherche (chargement, erreur, résultats
 * partiels, nombre d'objets trouvés) est annoncé par une région vivante hors de la liste.
 */
import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Boxes, Bot, Bug, ClipboardCheck, FileText, FlaskConical, Layers, Lightbulb, Columns3, Rocket, Scale,
  ShieldCheck, Tag, Timer, type LucideIcon,
} from 'lucide-react'

import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { rechercheApi, type ObjetTrouve, type TypeObjet } from '@/api/rechercheApi'
import { useChangerContexte } from '@/hooks/useChangerContexte'
import { useSession } from '@/hooks/useSession'
import { destination, normaliserRequete, requeteExploitable } from '@/lib/rechercheObjets'
import { TRANSVERSES, itemVisiblePourRoles, niveauxVisibles } from '@/lib/nav'
import { useT } from '@/i18n/language'
import { motifEcritureRefusee, type Session } from '@/auth/session'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  session: Session
  onNewProject: () => void
  /** Attente après la dernière frappe avant d'interroger la passerelle (ms) ; 0 dans les tests. */
  delaiRecherche?: number
}

const ICONES: Record<TypeObjet, LucideIcon> = {
  item: FileText, defect: Bug, test_case: FlaskConical, test_campaign: ClipboardCheck,
  requirement: ShieldCheck, adr: Scale, release: Tag, deployment: Rocket, mission: Bot,
  sprint: Timer, program: Layers, project: Boxes,
}

/** Valeur stabilisée après `delai` ms sans changement (immédiate si `delai` vaut 0). */
function useValeurDifferee(valeur: string, delai: number): string {
  const [differee, setDifferee] = useState(valeur)
  useEffect(() => {
    if (delai <= 0) return undefined
    const minuteur = window.setTimeout(() => setDifferee(valeur), delai)
    return () => window.clearTimeout(minuteur)
  }, [valeur, delai])
  return delai <= 0 ? valeur : differee
}

export function CommandPalette({ open, onOpenChange, session, onNewProject, delaiRecherche = 250 }: Props): JSX.Element {
  const navigate = useNavigate()
  const { t } = useT()
  const { changerContexte, choisirProjet } = useChangerContexte()
  // « Nouveau projet » se juge sur le JETON (N-43), comme dans le menu « Créer » : la prop `session` porte les
  // rôles EFFECTIFS du projet ouvert (un membre lecteur y devient `workspace_viewer`), et créer un projet ne
  // dépend pas du rôle tenu dans le projet ouvert — la passerelle accepte la création d'un jeton qui écrit.
  const jeton = useSession()
  const peutCreerUnProjet = motifEcritureRefusee(jeton) === null
  const [saisie, setSaisie] = useState('')
  // Une palette rouverte repart d'une saisie vide (pas d'anciens résultats sous les yeux).
  useEffect(() => { if (!open) setSaisie('') }, [open])

  const requete = useValeurDifferee(normaliserRequete(saisie), delaiRecherche)
  const exploitable = requeteExploitable(requete)
  const recherche = useQuery({
    // Portée du cache : l'organisation ET l'utilisateur (les résultats dépendent de ses adhésions).
    queryKey: ['recherche-objets', session.organisationId ?? null, session.userId ?? null, requete],
    queryFn: ({ signal }) => rechercheApi.rechercher(requete, { signal }),
    enabled: open && exploitable,
    staleTime: 30_000,
    retry: false,
  })
  const enAttente = exploitable && (recherche.isPending || recherche.isFetching
    || normaliserRequete(saisie) !== requete)
  const groupes = exploitable && recherche.data ? recherche.data.groups : []

  const libelleType = (type: TypeObjet): string => {
    switch (type) {
      case 'item': return t('rech.type.item', 'Items du backlog')
      case 'defect': return t('rech.type.defect', 'Anomalies')
      case 'test_case': return t('rech.type.test_case', 'Cas de test')
      case 'test_campaign': return t('rech.type.test_campaign', 'Campagnes de test')
      case 'requirement': return t('rech.type.requirement', 'Exigences')
      case 'adr': return t('rech.type.adr', 'Décisions (ADR)')
      case 'release': return t('rech.type.release', 'Versions')
      case 'deployment': return t('rech.type.deployment', 'Déploiements')
      case 'mission': return t('rech.type.mission', 'Missions')
      case 'sprint': return t('rech.type.sprint', 'Sprints')
      case 'program': return t('rech.type.program', 'Programmes')
      case 'project': return t('rech.type.project', 'Projets')
    }
  }
  const contexte = (o: ObjetTrouve): string => {
    if (o.type === 'project' || o.type === 'program') return ''
    if (o.workspace_name) return o.workspace_name
    if (o.program_name) return t('rech.contexteProgramme', 'Programme {{nom}}', { nom: o.program_name })
    return t('rech.contexteOrganisation', 'Organisation')
  }

  // Idée et story s'écrivent DANS le projet ouvert : les rôles effectifs de la prop `session`. « Nouveau
  // programme » suit le jeton, comme « Nouveau projet » (D-14 : la passerelle crée un programme sur le seul
  // rôle de realm).
  const creeDansLeProjet = session.roles.includes('workspace_contributor')

  const go = (path: string): void => {
    onOpenChange(false)
    navigate(path)
  }
  const create = (fn: () => void): void => {
    onOpenChange(false)
    fn()
  }
  const ouvrir = (o: ObjetTrouve): void => {
    onOpenChange(false)
    const d = destination(o)
    if (d.projet) choisirProjet(d.projet, { vers: d.chemin })
    else if (d.programme) changerContexte({ programme: d.programme }, { vers: d.chemin })
    else navigate(d.chemin)
  }

  // Ce que dit la région vivante (hors de la liste : une listbox ne contient que des options).
  let etat: { texte: string; ton: 'neutre' | 'erreur' } | null = null
  if (saisie.trim() !== '' && !requeteExploitable(saisie)) {
    etat = normaliserRequete(saisie).length > 100
      ? { texte: t('rech.tropLong', 'Recherche limitée à 100 caractères.'), ton: 'erreur' }
      : { texte: t('rech.aide', 'Objets : tapez au moins 2 caractères, ou un identifiant (#12, ANO-3, ADR-1).'), ton: 'neutre' }
  } else if (enAttente) {
    etat = { texte: t('rech.chargement', 'Recherche des objets…'), ton: 'neutre' }
  } else if (exploitable && recherche.isError) {
    etat = { texte: t('rech.erreur', 'La recherche des objets a échoué : {{message}}',
      { message: (recherche.error as Error).message }), ton: 'erreur' }
  } else if (exploitable && recherche.data) {
    const total = recherche.data.total
    const base = total === 0
      ? t('rech.aucunObjet', 'Aucun objet ne correspond à « {{q}} ».', { q: requete })
      : total === 1
        ? t('rech.unObjet', '1 objet trouvé.')
        : t('rech.nObjets', '{{n}} objets trouvés.', { n: String(total) })
    const partiel = recherche.data.partial
      ? ' ' + t('rech.partiel', 'Résultats partiels : {{types}} illisible(s) pour le moment.',
        { types: recherche.data.failed_types.map(libelleType).join(', ') })
      : ''
    etat = { texte: base + partiel, ton: recherche.data.partial ? 'erreur' : 'neutre' }
  }

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('cmdk.title', 'Palette de commandes')}
      description={t('cmdk.descriptionObjets', 'Rechercher un écran, une action ou un objet (identifiant ou texte)')}
    >
      <CommandInput placeholder={t('cmdk.searchPlaceholder', 'Aller à un écran, lancer une action…')}
                    value={saisie} onValueChange={setSaisie} maxLength={200} />
      <div role="status" aria-live="polite" data-testid="recherche-objets-etat"
           className={etat ? `flex items-center gap-2 border-b px-3 py-1.5 text-xs ${etat.ton === 'erreur' ? 'text-destructive' : 'text-muted-foreground'}` : 'sr-only'}>
        {etat ? <span className="min-w-0 flex-1">{etat.texte}</span> : null}
        {exploitable && recherche.isError && !enAttente ? (
          <Button type="button" size="sm" variant="outline" className="h-7"
                  onClick={() => void recherche.refetch()}>
            {t('rech.reessayer', 'Réessayer')}
          </Button>
        ) : null}
      </div>
      <CommandList>
        {/* Rien tant que la recherche d'objets répond : « Aucun résultat » serait faux. */}
        <CommandEmpty>{enAttente ? null : t('cmdk.emptyState', 'Aucun résultat.')}</CommandEmpty>

        {groupes.map((g) => (
          <CommandGroup key={`objets-${g.type}`} data-testid={`recherche-groupe-${g.type}`}
                        heading={g.truncated
                          ? t('rech.groupeTronque', '{{type}} — premiers résultats, précisez la recherche', { type: libelleType(g.type) })
                          : libelleType(g.type)}>
            {g.results.map((o) => {
              const Icone = ICONES[o.type]
              const ou = contexte(o)
              return (
                <CommandItem key={`${o.type}:${o.id}`} value={`objet ${o.type} ${o.id}`}
                             keywords={[requete, o.reference ?? '', o.title]}
                             data-testid={`recherche-objet-${o.type}-${o.id}`}
                             onSelect={() => ouvrir(o)}>
                  <Icone aria-hidden="true" />
                  {o.reference ? <span className="shrink-0 font-mono text-xs text-muted-foreground">{o.reference}</span> : null}
                  <span className="min-w-0 truncate">{o.title}</span>
                  {ou ? <span className="ml-auto max-w-[40%] shrink-0 truncate text-xs text-muted-foreground">{ou}</span> : null}
                </CommandItem>
              )
            })}
          </CommandGroup>
        ))}
        {groupes.length > 0 ? <CommandSeparator /> : null}

        {/* v030-po-ux-01 (r2) : DÉFAUT MESURÉ EN NAVIGATEUR RÉEL — ce groupe listait ces
            quatre actions INCONDITIONNELLEMENT, en dehors de `NAV_GROUPS`/
            `itemVisiblePourRoles`, donc jamais couvert par le rôle effectif par espace
            (r1). Un lecteur réel de l'espace ouvert voyait "Nouvelle idée"/"Nouvelle
            story"/"Nouveau programme" alors que les écrans cibles (/ideas, /backlog,
            /programs) refusent déjà l'écriture pour ce rôle (`canWrite`, corrigé en r2) —
            la palette annonçait une action que l'écran suivant, lui, refuse. "Nouveau
            projet" est jugé sur le JETON, jamais sur le rôle tenu dans l'espace ouvert :
            créer un ESPACE entièrement nouveau n'en dépend pas (N-43, comme le menu
            « Créer »). Un jeton lecteur ou administrateur sans rôle d'écriture n'y est
            plus offert ce que la passerelle refuse ; un jeton qui écrit l'y garde, même
            lecteur du projet ouvert. D-14 (audit du 2026-10-09) : « Nouveau programme » suit
            lui aussi le JETON — la passerelle crée un programme sur le seul rôle de realm, et
            /programs l'offre à un jeton qui écrit ; seuls « Nouvelle idée » et « Nouvelle
            story » restent jugées dans le projet ouvert (/ideas, /backlog refusent l'écriture
            au lecteur). */}
        {peutCreerUnProjet || creeDansLeProjet ? (
          <>
            <CommandGroup heading={t('cmdk.createGroup', 'Créer')}>
              {peutCreerUnProjet ? (
                <CommandItem value={t('create.project', 'Nouveau projet')}
                             onSelect={() => create(onNewProject)}>
                  <Boxes aria-hidden="true" />
                  <span>{t('create.project', 'Nouveau projet')}</span>
                </CommandItem>
              ) : null}
              {creeDansLeProjet ? (
                <>
                  <CommandItem value={t('create.idea', 'Nouvelle idée')}
                               onSelect={() => create(() => navigate('/ideas'))}>
                    <Lightbulb aria-hidden="true" />
                    <span>{t('create.idea', 'Nouvelle idée')}</span>
                  </CommandItem>
                  <CommandItem value={t('create.story', 'Nouvelle story')}
                               onSelect={() => create(() => navigate('/projet?vue=tableau'))}>
                    <Columns3 aria-hidden="true" />
                    <span>{t('create.story', 'Nouvelle story')}</span>
                  </CommandItem>
                </>
              ) : null}
              {peutCreerUnProjet ? (
                <CommandItem value={t('create.program', 'Nouveau programme')}
                             onSelect={() => create(() => navigate('/programs'))}>
                  <Layers aria-hidden="true" />
                  <span>{t('create.program', 'Nouveau programme')}</span>
                </CommandItem>
              ) : null}
            </CommandGroup>
            <CommandSeparator />
          </>
        ) : null}

        {/* U6b vague 1 : un groupe par niveau (Général, puis Projet, Programme, Organisation,
            Plateforme pour le super-administrateur) ; même registre que les barres latérales. */}
        {[
          { cle: 'general', label: t('cmdk.general', 'Général'), items: TRANSVERSES },
          ...niveauxVisibles(session.roles).map((n) => ({
            cle: n.id, label: t(`nav.titreNiveau.${n.id}`, n.label), items: n.groupes.flatMap((g) => g.items),
          })),
        ].map((groupe) => {
          const items = groupe.items.filter((i) => itemVisiblePourRoles(i, session.roles))
          if (items.length === 0) return null
          return (
            <CommandGroup key={groupe.cle} heading={groupe.label}>
              {items.map((item) => {
                const label = t(`nav.${item.path}`, item.label)
                const desc = t(`navdesc.${item.path}`, item.description)
                return (
                  <CommandItem
                    key={item.path}
                    value={`${groupe.label} ${label} ${desc}`}
                    onSelect={() => go(item.path)}
                  >
                    <item.icon aria-hidden="true" />
                    <span>{label}</span>
                    {item.etat === '0.5.x' ? (
                      <span className="rounded-full bg-brand-subtle px-1.5 text-[0.625rem] font-medium text-brand-text">0.5.x</span>
                    ) : null}
                    <span className="ml-auto truncate text-xs text-muted-foreground">
                      {desc}
                    </span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          )
        })}
      </CommandList>
    </CommandDialog>
  )
}
